import {inject, Injectable} from '@angular/core';
import {interval, Subscription} from 'rxjs';
import {SlideshowStateService} from './slideshow-state.service';
import {VideoControlService} from './video-control.service';

@Injectable({
  providedIn: 'root'
})
export class SlideshowControlService {

  private slideshowStateService = inject(SlideshowStateService);
  private videoControlService = inject(VideoControlService);

  // Slideshow interval subscription
  private slideshowInterval: Subscription | null = null;

  /**
   * Start the slideshow interval for images, handle videos appropriately
   */
  startSlideshow(
    mediaItems: { url: string; type: 'image' | 'video'; duration?: number }[],
    currentMediaIndex: number,
    onAdvanceCallback: () => void
  ): void {
    // Clear any existing interval first
    this.pauseSlideshow();

    if (mediaItems.length === 0) return;

    const currentItem = mediaItems[currentMediaIndex];
    if (!currentItem) return;

    // Handle differently based on media type
    if (currentItem.type === 'video') {
      // For videos, we don't use an interval - we'll advance when the video ends
      this.slideshowStateService.setVideoPlaying(true);

      // Play the current video
      setTimeout(() => {
        this.videoControlService.playVideoByIndex(currentMediaIndex).catch(error => {
          console.error('Error starting video playback:', error);
          // If video fails, advance to next item
          onAdvanceCallback();
        });
      }, 300);
    } else {
      // For images, use a 4-second interval
      this.slideshowStateService.setVideoPlaying(false);

      // Create a new interval subscription for advancing images
      this.slideshowInterval = interval(4000).subscribe(() => {
        onAdvanceCallback();
      });
    }
  }

  /**
   * Pause the slideshow interval and any playing videos
   */
  pauseSlideshow(): void {
    // Unsubscribe from the current interval if it exists
    if (this.slideshowInterval) {
      this.slideshowInterval.unsubscribe();
      this.slideshowInterval = null;
    }

    // Pause any playing videos
    this.videoControlService.pauseCurrentVideo();
  }

  /**
   * Advance to the next media item in the slideshow
   */
  advanceToNextMedia(
    mediaItems: { url: string; type: 'image' | 'video'; duration?: number }[],
    currentMediaIndex: number,
    onIndexUpdate: (newIndex: number) => void,
    onRestartSlideshow: () => void
  ): void {
    if (mediaItems.length === 0) return;

    let nextIndex = currentMediaIndex + 1;

    // If we've reached the end, loop back to the beginning
    if (nextIndex >= mediaItems.length) {
      nextIndex = 0;
    }

    console.log(`Advancing from media ${currentMediaIndex} to ${nextIndex}`);

    // Stop any currently playing videos
    this.videoControlService.stopAllVideos();

    // Update the current index
    onIndexUpdate(nextIndex);

    // Restart the slideshow with the new media item
    onRestartSlideshow();
  }

  /**
   * Go to previous media item
   */
  goToPreviousMedia(
    mediaItems: { url: string; type: 'image' | 'video'; duration?: number }[],
    currentMediaIndex: number,
    onIndexUpdate: (newIndex: number) => void,
    onRestartSlideshow: () => void
  ): void {
    if (mediaItems.length === 0) return;

    let previousIndex = currentMediaIndex - 1;

    // If we've reached the beginning, loop to the end
    if (previousIndex < 0) {
      previousIndex = mediaItems.length - 1;
    }

    console.log(`Going to previous media: ${currentMediaIndex} → ${previousIndex}`);

    // Stop any currently playing videos
    this.videoControlService.stopAllVideos();

    // Update the current index
    onIndexUpdate(previousIndex);

    // Restart the slideshow with the new media item
    onRestartSlideshow();
  }

  /**
   * Jump to specific media item by index
   */
  jumpToMedia(
    mediaItems: { url: string; type: 'image' | 'video'; duration?: number }[],
    targetIndex: number,
    currentMediaIndex: number,
    onIndexUpdate: (newIndex: number) => void,
    onRestartSlideshow: () => void
  ): void {
    if (targetIndex < 0 || targetIndex >= mediaItems.length || targetIndex === currentMediaIndex) {
      return;
    }

    console.log(`Jumping to media ${targetIndex}`);

    // Stop any currently playing videos
    this.videoControlService.stopAllVideos();

    // Update the current index
    onIndexUpdate(targetIndex);

    // Restart the slideshow with the new media item
    onRestartSlideshow();
  }

  /**
   * Check if slideshow is currently running
   */
  isRunning(): boolean {
    return this.slideshowInterval !== null;
  }

  /**
   * Get current slideshow state
   */
  getSlideshowState(): {
    isRunning: boolean;
    isVideoPlaying: boolean;
    hasInterval: boolean;
  } {
    return {
      isRunning: this.isRunning(),
      isVideoPlaying: this.slideshowStateService.isVideoPlaying(),
      hasInterval: this.slideshowInterval !== null
    };
  }

  /**
   * Get optimal image display duration based on media type and count
   */
  getOptimalImageDuration(mediaItems: { url: string; type: 'image' | 'video' }[]): number {
    const imageCount = mediaItems.filter(item => item.type === 'image').length;

    // Adjust duration based on number of images
    if (imageCount > 20) {
      return 3000; // 3 seconds for many images
    } else if (imageCount > 10) {
      return 3500; // 3.5 seconds for moderate number
    } else if (imageCount < 3) {
      return 5000; // 5 seconds for few images
    }

    return 4000; // default 4 seconds
  }

  /**
   * Reset the service state
   */
  reset(): void {
    this.pauseSlideshow();
  }

  /**
   * Cleanup when service is destroyed
   */
  cleanup(): void {
    this.pauseSlideshow();
  }
} 
